/**
 * Bol e-commerce data hooks — React Query üzerinden.
 *
 * EcommerceTab.js'in büyük state karmaşasını yavaş yavaş bu hook'lara devretmek için.
 * Şu an için orders fetch ve preview/create/recover mutation'ları sağlıyor.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── QUERIES ─────────────────────────────────────────────────────────────────

export function useBolOrders({ status = 'open', limit = 1000, onlyPending = false } = {}) {
    return useQuery({
        queryKey: ['bol-orders', { status, limit, onlyPending }],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            params.append('limit', String(limit));
            if (onlyPending) params.append('only_pending', 'true');
            const { data } = await axios.get(`${API}/bol/orders?${params}`, {
                headers: authHeaders(),
            });
            return data;
        },
    });
}

export function useBolDashboard({ period = '30d' } = {}) {
    return useQuery({
        queryKey: ['bol-dashboard', period],
        queryFn: async () => {
            const { data } = await axios.get(`${API}/bol/dashboard`, {
                headers: authHeaders(),
                params: { period },
            });
            return data;
        },
        staleTime: 60_000,
    });
}

// Iter 110/Faz3: config'i React Query'e taşı — useEffect/axios.get'ten kurtul
export function useBolConfig() {
    return useQuery({
        queryKey: ['bol-config'],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`${API}/bol/config`, { headers: authHeaders() });
                return data;
            } catch {
                return {};  // config yok = needsSetup
            }
        },
        staleTime: 5 * 60_000,
        retry: false,
    });
}

// ─── MUTATIONS (cache invalidation ile) ──────────────────────────────────────

export function useBolPreviewLabels() {
    return useMutation({
        mutationFn: async (orderIds) => {
            const { data } = await axios.post(
                `${API}/bol/labels/preview`,
                { order_ids: orderIds },
                { headers: authHeaders() },
            );
            return data;
        },
    });
}

export function useBolCreateLabels() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (items) => {
            const resp = await axios.post(
                `${API}/bol/labels/create`,
                { items },
                { headers: authHeaders(), responseType: 'blob' },
            );
            return resp;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}

export function useBolSyncMeta() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axios.post(
                `${API}/bol/labels/sync-meta`,
                {},
                { headers: authHeaders() },
            );
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}

export function useBolRecoverLabels() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ orderIds = [], includePickslip = true } = {}) => {
            const resp = await axios.post(
                `${API}/bol/labels/recover`,
                { order_ids: orderIds, include_pickslip: includePickslip },
                { headers: authHeaders(), responseType: 'blob' },
            );
            return resp;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}

// ─── SPLIT SHIPMENT ──────────────────────────────────────────────────────────

export function useBolSplitPreview() {
    return useMutation({
        mutationFn: async ({ orderId, boxes }) => {
            const { data } = await axios.post(
                `${API}/bol/labels/split-preview`,
                { order_id: orderId, boxes },
                { headers: authHeaders() },
            );
            return data;
        },
    });
}

export function useBolSplitCreate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ orderId, boxes }) => {
            const resp = await axios.post(
                `${API}/bol/labels/split-create`,
                { order_id: orderId, boxes },
                { headers: authHeaders(), responseType: 'blob' },
            );
            return resp;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}
